import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { ModalCenter, type ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Combobox } from '@shared/components/ui/AdvancedFilter/components';
import { forwardRef, useState, useEffect, useMemo, useImperativeHandle, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useCountries } from '../../../hooks';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/pro-light-svg-icons';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { useToast } from '@shared/hooks/use-toast';
import { ShippingDataErrorApiAdapter } from '../../../adapters/ShippingDataErrorApiAdapter';
import { translateOrderApiError } from '../../../../CreateEditOrders/utils/errorTranslator';

export interface ShippingDataErrorModalRef {
  openModal: (orderId: number) => void;
  closeModal: () => void;
}

interface ShippingDataErrorModalProps {
  customer?: {
    name?: string;
    email?: string;
    phone?: string;
    address?: {
      address1?: string;
      address2?: string;
      zip?: string;
      city?: string;
      province?: string;
      country_code?: string;
    };
  };
  onSuccess?: () => void;
}

const ShippingDataErrorModalComponent: React.ForwardRefRenderFunction<
  ShippingDataErrorModalRef,
  ShippingDataErrorModalProps
> = ({ customer, onSuccess }, ref) => {
  const { t } = useTranslation('viewOrdersModule');
  const { toast } = useToast();
  const { countries, loading: countriesLoading } = useCountries();
  const modalRef = useRef<ModalCenterRef>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentOrderId, setCurrentOrderId] = useState<number | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address1: '',
    address2: '',
    postalCode: '',
    city: '',
    province: '',
    country: '',
  });

  useEffect(() => {
    if (customer) {
      setFormData({
        name: customer.name || '',
        email: customer.email || '',
        phone: customer.phone || '',
        address1: customer.address?.address1 || '',
        address2: customer.address?.address2 || '',
        postalCode: customer.address?.zip || '',
        city: customer.address?.city || '',
        province: customer.address?.province || '',
        country: customer.address?.country_code || '',
      });
    }
  }, [customer]);

  useImperativeHandle(ref, () => ({
    openModal: (orderId: number) => {
      setCurrentOrderId(orderId);
      modalRef.current?.openModal();
    },
    closeModal: () => modalRef.current?.closeModal(),
  }));

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleClose = () => {
    modalRef.current?.closeModal();
  };

  const handleBack = () => {
    modalRef.current?.closeModal();
  };

  const handleSave = async () => {
    if (!currentOrderId) {
      toast({
        title: t('shippingDataError.error.title'),
        description: 'ID del pedido no encontrado',
        variant: 'destructive',
      });
      return;
    }

    // Validar campos requeridos
    if (!formData.address1 || !formData.postalCode || !formData.city) {
      toast({
        title: t('shippingDataError.error.title'),
        description: 'Debes completar todos los campos requeridos',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const httpClient = createHttpClient(apiClientWH);
      const adapter = new ShippingDataErrorApiAdapter(httpClient);

      const requestData = {
        customer: {
          address: {
            address1: formData.address1,
            address2: formData.address2 || '',
            zip: formData.postalCode,
            city: formData.city,
            province: formData.province || '',
            country_code: formData.country || '',
          },
        },
      };

      console.log('🔍 ShippingDataErrorModal - Sending data:', {
        orderId: currentOrderId,
        data: requestData,
      });

      const response = await adapter.solveShippingDataError(currentOrderId, requestData);

      console.log('🔍 ShippingDataErrorModal - Response:', response);

      if (response.success) {
        toast({
          title: t('shippingDataError.success.title'),
          description: response.message,
          variant: 'default',
        });

        // Ejecutar callback de éxito si existe
        onSuccess?.();
      } else {
        toast({
          title: t('shippingDataError.error.title'),
          description: response.message,
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      console.log('🔍 ShippingDataErrorModal - Error capturado:', {
        error,
        errorType: typeof error,
        errorMessage: error?.message,
        errorResponse: error?.response,
        errorResponseData: error?.response?.data,
        errorResponseStatus: error?.response?.status,
        errorStatusCode: error?.statusCode,
        errorData: error?.data,
        fullError: JSON.stringify(error, null, 2),
      });

      // Traducir el error usando el sistema de traducción
      const translatedError = translateOrderApiError(error, t);

      console.log('🔍 ShippingDataErrorModal - Error traducido:', {
        originalError: error?.message || error?.response?.data?.message || error?.response?.data?.error?.message,
        translatedError,
      });

      toast({
        title: t('shippingDataError.error.title'),
        description: translatedError,
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const countryOptions = useMemo(() => {
    return countries.map((country) => ({
      label: country.name,
      value: country.code,
    }));
  }, [countries]);

  return (
    <ModalCenter
      ref={modalRef}
      title={t('shippingDataError.title')}
      icon={
        <button
          onClick={handleBack}
          className="flex items-center justify-center w-6 h-6 hover:bg-hover-press rounded transition-colors"
        >
          <FontAwesomeIcon icon={faChevronLeft} className="w-4 h-4" />
        </button>
      }
      width="lg"
      type="default"
      fullScreenMobile={true}
      className="max-h-[90vh]"
    >
      <div className="flex flex-col h-full">
        {/* Contenido principal */}
        <div className="flex-1 flex flex-col gap-6 py-4 text-sm max-h-[70vh] overflow-y-auto pb-24 md:pb-4">
          {/* Descripción del error */}
          <p className="text-sm">{t('shippingDataError.description')}</p>

          <div className="flex flex-col gap-4">
            <div>
              <label htmlFor="address1" className="font-medium">
                {t('shippingDataError.address1')} <span className="text-error">*</span>
              </label>
              <Input
                id="address1"
                placeholder={t('shippingDataError.placeholders.address1')}
                className="mt-1"
                value={formData.address1}
                onChange={(e) => handleInputChange('address1', e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="address2" className="font-medium">
                {t('shippingDataError.address2')}
              </label>
              <Input
                id="address2"
                placeholder={t('shippingDataError.placeholders.address2')}
                className="mt-1"
                value={formData.address2}
                onChange={(e) => handleInputChange('address2', e.target.value)}
              />
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label htmlFor="postalCode" className="font-medium">
                  {t('shippingDataError.postalCode')} <span className="text-error">*</span>
                </label>
                <Input
                  id="postalCode"
                  placeholder={t('shippingDataError.placeholders.postalCode')}
                  className="mt-1"
                  value={formData.postalCode}
                  onChange={(e) => handleInputChange('postalCode', e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="city" className="font-medium">
                  {t('shippingDataError.city')} <span className="text-error">*</span>
                </label>
                <Input
                  id="city"
                  placeholder={t('shippingDataError.placeholders.city')}
                  className="mt-1"
                  value={formData.city}
                  onChange={(e) => handleInputChange('city', e.target.value)}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label htmlFor="province" className="font-medium">
                  {t('shippingDataError.province')}
                </label>
                <Input
                  id="province"
                  placeholder={t('shippingDataError.placeholders.province')}
                  className="mt-1"
                  value={formData.province}
                  onChange={(e) => handleInputChange('province', e.target.value)}
                />
              </div>
              <div>
                <label className="font-medium">{t('shippingDataError.country')}</label>
                <Combobox
                  options={countryOptions}
                  value={formData.country}
                  setValue={(value: string) => handleInputChange('country', value)}
                  placeholder={t('shippingDataError.placeholders.country')}
                  disabled={countriesLoading}
                  className="w-full mt-1"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Botones de acción - Fijos en mobile */}
        <div className="fixed bottom-0 left-0 right-0 p-4 md:pb-0 md:px-0 md:relative md:border-t-0">
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="lg" onClick={handleClose}>
              {t('shippingDataError.cancel')}
            </Button>
            <Button variant="primary" size="lg" onClick={handleSave} disabled={isSubmitting}>
              {isSubmitting ? t('shippingDataError.submitting') : t('shippingDataError.save')}
            </Button>
          </div>
        </div>
      </div>
    </ModalCenter>
  );
};

export const ShippingDataErrorModal = forwardRef(ShippingDataErrorModalComponent);

ShippingDataErrorModal.displayName = 'ShippingDataErrorModal';
